import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

// One line of the organization's Material Takeoff catalog (see
// backend/apexos/apps/measurements/takeoff_defaults.py for the formula).
export interface TakeoffLineItem {
  id: number;
  name: string;
  category: 'field_materials' | 'accessories' | 'fasteners_misc';
  unit_label: string;
  driver:
    | 'squares'
    | 'true_area_sqft'
    | 'ridge_lf'
    | 'hip_lf'
    | 'valley_lf'
    | 'eave_lf'
    | 'rake_lf'
    | 'eave_plus_rake_lf'
    | 'valleys_plus_eaves_lf'
    | 'fixed';
  multiplier: string;
  coverage_per_unit: string;
  include_waste: boolean;
  min_quantity: number;
  applies_to: string[];
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type TakeoffLineItemInput = Omit<TakeoffLineItem, 'id' | 'created_at' | 'updated_at'>;

// Fetch the org catalog (backend seeds the defaults on first request)
export function useTakeoffItems() {
  return useQuery({
    queryKey: ['takeoff-items'],
    queryFn: async () => {
      const response = await api.get('/api/takeoff-items/');
      return response.data as TakeoffLineItem[];
    },
  });
}

export function useCreateTakeoffItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<TakeoffLineItemInput>) => {
      const response = await api.post('/api/takeoff-items/', data);
      return response.data as TakeoffLineItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['takeoff-items'] });
    },
  });
}

export function useUpdateTakeoffItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<TakeoffLineItemInput> }) => {
      const response = await api.patch(`/api/takeoff-items/${id}/`, data);
      return response.data as TakeoffLineItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['takeoff-items'] });
    },
  });
}

export function useDeleteTakeoffItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/takeoff-items/${id}/`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['takeoff-items'] });
    },
  });
}

// Replace the org's catalog with the built-in defaults
export function useResetTakeoffItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/takeoff-items/reset-defaults/', {});
      return response.data as TakeoffLineItem[];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['takeoff-items'] });
    },
  });
}
